<template>
  <div :class="['content', programStyle]" v-if="form">
    <div class="appeal-header-container">
      <h1>{{ $t('appeal-review-tracking') }}</h1>
      <div class="flex-column">
        <h2>{{ $t('request-details') }}</h2>
        <div class="flex-column form-info">
          <div class="item flex-row">
            <span class="label">{{ $t('dealer') }}:</span>
            <span>{{ selectedForm.dealerName }}</span>
          </div>
          <div class="item flex-row">
            <span class="label">{{ $t('dealer-code') }}:</span>
            <span>{{ selectedForm.entity }}</span>
          </div>
          <div class="item flex-row">
            <span class="label">{{ $t('BC') }}:</span>
            <span>{{ selectedForm.bc }}</span>
          </div>
          <div class="item flex-row">
            <span class="label">{{ $t('sales-district') }}:</span>
            <span>{{ selectedForm.salesDistrict }}</span>
          </div>
          <div class="item flex-row">
            <span class="label">{{ $t('service-district') }}: </span>
            <span>{{ selectedForm.serviceDistrict }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="appeal-status-container">
      <FieldAppealDataTable :headings="tableHeadings" :rows="stepRows" :perPage="rowCount" :page="currentPage" />
    </div>
    <div class="form-container" v-if="appeals" id="dials">
      <div class="item-container" v-for="(appeal, index1) in appeals" :key="appeal.dialId">
        <div class="appeal-info-container">
          <div class="left-container flex-column">
            <div class="dial-container" @click="selectDial(appeal)">
              <div v-if="appeal.dialTypeId === 'SMMT' || appeal.dialTypeId === 'FDMT'">
                <IconProgress :progress="appeal.progress" :targets="appeal.dialTargets" :color="appeal.dialColor" />
                <div class="smmtInner" v-if="appeal.dialResultText || appeal.progress">
                  {{ appeal.dialResultText || appeal.progress }}
                </div>
              </div>
              <div
                @click="selectDial(appeal)"
                v-if="appeal.dialTypeId !== 'SMMT' && appeal.dialTypeId !== 'FDMT'"
                :class="['circle', appeal.dialColor]"
              >
                <div class="inner" v-if="appeal.dialResultText">
                  {{ appeal.dialResultText }}
                </div>
              </div>
            </div>
            <Multiselect
              v-if="lineItemReviewer && (!appeal.statuses || editMode)"
              v-model="appeal.value"
              :options="
                appeal.dialTypeId === 'SMMT' || appeal.dialTypeId === 'FDMT'
                  ? multiTargetOptions(appeal.dialTargets)
                  : lineItemOptions
              "
              class="multiselect single"
              track-by="value"
              label="label"
              :placeholder="$t('no-action')"
              :allow-empty="false"
              :showLabels="false"
              :searchable="false"
              :disabled="appeal.disableDial"
              :class="{
                appealApproved:
                  appeal.value.value === 'Approved' ||
                  appeal.value.value === 'target-1' ||
                  appeal.value.value === 'target-2' ||
                  appeal.value.value === 'target-3',
                appealDeclined: appeal.value.value === 'Declined',
              }"
            ></Multiselect>
            <div v-if="appeal.statuses && !editMode">
              {{ appeal.statuses.result }}
            </div>
          </div>
          <div class="right-container flex-column">
            <h2>{{ appeal.dialTitle }}</h2>
            <div v-if="editMode && appeal.value.value === 'Not Appealing'" class="checkbox-container">
              <Checkbox :value="true" :label="checkboxLabel" :disabled="true" />
            </div>
            <div v-if="!editMode && appeal.notAppealed" class="checkbox-container">
              <Checkbox v-model="appeal.notAppealed" :label="checkboxLabel" :disabled="true" />
            </div>
            <div class="description">{{ $t('detailed-reason-for-appeal') }}</div>
            <textarea
              class="editMode"
              v-if="
                editMode &&
                (appeal.value.value === 'Approved' ||
                  appeal.value.value === 'Declined' ||
                  appeal.value.value === 'no-action')
              "
              v-model="appeal.reasonForAppeal"
            />
            <textarea
              :class="{ notAppealed: appeal.notAppealed }"
              readonly
              v-if="appeal.reasonForAppeal && !editMode && !appeal.notAppealed"
              v-model="appeal.reasonForAppeal"
            />
            <textarea
              :class="{ notAppealed: appeal.notAppealed }"
              readonly
              v-if="appeal.reasonForAppeal && !editMode && appeal.notAppealed"
              v-model="checkboxReason"
            />
            <textarea
              :class="{ notAppealed: appeal.notAppealed }"
              readonly
              v-if="appeal.reasonForAppeal && editMode && appeal.value.value === 'Not Appealing'"
              v-model="checkboxReason"
            />
            <div>{{ $t('supporting-documents-0') }}</div>
            <div v-if="appeal.uploads">
              <div class="support-docs" v-for="upload in appeal.uploads" :key="upload.key">
                <span v-if="upload.label === appeal.dialTitle">{{ upload.originalFilename }}</span>
                <a class="relative" target="_blank" :href="upload.url">
                  <img class="download-icon" src="@/assets/img/download-arrow.png" />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div v-if="domain === 'US EL'">
          <div v-for="(dialArr, index2) in appeal.dialDataArr" :key="dialArr.dialId">
            <div v-if="index1 === index2">
              <SMMTFieldAppeal
                ref="smmtFieldAppeal"
                :dialInfo="dialArr"
                :disabled="!form.permissions[0].canSubmit"
                @smmtValidation="smmtValidation"
              />
              <div class="helper-text-container">
                <span class="helper-text"
                  >Please provide reasons for each decision you make, outlining specific requirements, "see documents"
                  is not a valid reason.</span
                >
                <span class="helper-text red"
                  >Details available to appeal are highlighted in <span class="bold">red</span>.</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="step-container flex-column" v-if="stepRows && domain !== 'US EL'">
        <div
          :class="['steps flex-column', status.id ? 'border-bottom' : '']"
          v-for="(status, index) in stepRows"
          :key="status.stepName"
        >
          <div class="bottom-info" v-if="(status.id || status.formId) && status.ordinal !== 0">
            <span class="info">{{ status.stepName }}</span>
            <div class="dial" v-if="!status.dialId && status.result !== 'Complete'">
              {{ $t('select-one') }}
              <div class="flex-row radio-container">
                <button :class="status.result === 'Approved' ? 'radio-selected' : 'radio'">
                  <div v-if="status.result === 'Approved'" class="selected"></div>
                </button>
                <span>{{ $t('approve-appeal') }}</span>
              </div>
              <div class="flex-row radio-container">
                <button :class="status.result === 'Declined' ? 'radio-selected' : 'radio'">
                  <div v-if="status.result === 'Declined'" class="selected"></div>
                </button>
                <span>{{ $t('deny-appeal') }}</span>
              </div>
            </div>
            <span class="label">{{ $t('reason-0') }}: </span>
            <textarea v-if="!editMode || stepRows.length - 1 !== index" readonly v-model="status.reason" />
            <GInput
              v-if="editMode && stepRows.length - 1 === index"
              type="textarea"
              v-model="reason"
              maxlength="1000"
              :showCount="true"
            ></GInput>
            <span class="label" v-if="form.permissions[0].canViewNotes && status.notes">Notes:</span>
            <textarea v-if="form.permissions[0].canViewNotes && status.notes" readonly v-model="status.notes" />
            <div class="flex-row input-info">
              <div class="flex-column">
                <span class="label">{{ $t('name') }}:</span>
                <input readonly v-model="status.name" />
              </div>
              <div class="flex-column">
                <span class="label">{{ $t('title') }}:</span>
                <input readonly v-model="status.title" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bottom-container flex-column">
        <div
          class="bottom-info"
          v-if="
            form.overallStatus !== 'completed' &&
            (form.permissions[0].canSubmit || form.permissions[0].canLineItemSubmit)
          "
        >
          <div class="dial" v-if="!lineItemReviewer && domain !== 'US EL'">
            {{ $t('select-one') }}
            <div class="flex-row radio-container">
              <button :class="result === 'Approved' ? 'radio-selected' : 'radio'" @click="result = 'Approved'">
                <div v-if="result === 'Approved'" class="selected"></div>
              </button>
              <span>{{ $t('approve-appeal') }}</span>
            </div>
            <div class="flex-row radio-container">
              <button :class="result === 'Declined' ? 'radio-selected' : 'radio'" @click="result = 'Declined'">
                <div v-if="result === 'Declined'" class="selected"></div>
              </button>
              <span>{{ $t('deny-appeal') }}</span>
            </div>
          </div>
          <span v-if="domain !== 'US EL'" class="label">{{ $t('reason-0') }}:</span>
          <GInput
            v-if="domain !== 'US EL'"
            type="textarea"
            v-model="reason"
            maxlength="1000"
            :showCount="true"
          ></GInput>
          <div v-if="comments.length > 0" class="comments-container flex-column">
            <span class="label">{{ $t('additional-comments') }}:</span>
            <div class="comments-row">
              <span class="comments" v-for="comment in comments" :key="comment"
                >{{ comment }}<span class="comment-comma">, </span></span
              >
            </div>
          </div>
          <span class="label" v-if="form.permissions[0].canAddNotes && notes">Notes:</span>
          <textarea v-if="form.permissions[0].canAddNotes && notes" readonly v-model="notes" />
          <div v-if="form.permissions[0].canAddNotes" class="notes-button-container flex-row">
            <button v-if="form.permissions[0].canAddNotes && !notes" class="small-btn" @click="isModalOpen = 'true'">
              {{ $t('add-notes') }}
            </button>
            <button v-if="form.permissions[0].canAddNotes && notes" @click="isModalOpen = 'true'">
              {{ $t('view-update-notes') }}
            </button>
          </div>
          <div class="flex-row input-info">
            <div class="flex-column">
              <span class="label">* {{ $t('name') }}:</span>
              <input readonly v-model="name" />
            </div>
            <div class="flex-column">
              <span class="label">* {{ $t('title') }}:</span>
              <input v-model="title" />
            </div>
            <p class="signature-label">* Signature:</p>
            <div class="signature-section flex-column">
              <div class="flex-row signature-input">
                <div class="signature-icon"><SignatureIcon /></div>
                <VueSignaturePad id="signature" width="400px" height="100px" ref="signaturePad" :options="options" />
              </div>
              <span class="signature-confirmation" v-if="signature">{{ $t('signature-saved') }}</span>
              <div class="signature-buttons flex-row">
                <span class="custom-input-buttons flex-row">
                  <span class="edit-button" @click="saveSignature">Save Signature</span><span class="separator">|</span
                  ><span class="edit-button" @click="clearSignature">{{ $t('clear') }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="button-container">
          <button class="small-btn" @click="exit">{{ $t('exit') }}</button>
          <button
            v-if="form.permissions[0].canSubmit || form.permissions[0].canLineItemSubmit"
            value="save"
            @click="save"
            :class="[submitValidationCheck ? '' : 'disabled', 'small-btn']"
          >
            {{ $t('submit') }}
          </button>
          <button
            v-if="editMode"
            value="edit"
            @click="editFieldFormWithLineItems"
            :class="[lineItemEditValidationCheck ? '' : 'disabled', 'small-btn']"
          >
            {{ $t('submit') }}
          </button>
          <button class="small-btn" v-if="form.permissions[0].canEdit && !isFormFinalized" @click="openEditModal">
            {{ $t('edit') }}
          </button>
          <button
            class="small-btn"
            v-if="form.permissions[0].canLineItemEdit && !editMode && !isFormFinalized"
            @click="editLineItems"
          >
            {{ $t('edit') }}
          </button>
          <div v-if="invalidPermissions">
            {{ $t('contact-admin') }}
          </div>
          <button
            :class="[isFormFinalized ? 'disabled' : '', 'small-btn']"
            v-if="form.permissions[0].canReset"
            :disabled="isFormFinalized"
            @click="isResetFormModalOpen = 'true'"
          >
            {{ $t('reset-form') }}
          </button>
        </div>
      </div>
    </div>
    <Modal class="modal-screens" v-show="isModalOpen">
      <template v-slot:body class="body">
        <GInput type="textarea" v-model="notes" maxlength="1000" :showCount="true"></GInput>
        <div class="modal-button-container">
          <button class="submit" @click="setNotes">{{ $t('save') }}</button>
          <button @click="cancelNotes">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
    <Modal class="modal-screens" v-show="isEditModalOpen">
      <template v-slot:body class="body">
        <div class="modal-container">
          <div class="modal-header">{{ selectedForm.mostRecentStepName }}</div>
          <div class="bottom-info">
            <div class="dial" v-if="!lineItemReviewer">
              {{ $t('select-one') }}
              <div class="flex-row radio-container">
                <button :class="result === 'Approved' ? 'radio-selected' : 'radio'" @click="result = 'Approved'">
                  <div v-if="result === 'Approved'" class="selected"></div>
                </button>
                <span>{{ $t('approve-appeal') }}</span>
              </div>
              <div class="flex-row radio-container">
                <button :class="result === 'Declined' ? 'radio-selected' : 'radio'" @click="result = 'Declined'">
                  <div v-if="result === 'Declined'" class="selected"></div>
                </button>
                <span>{{ $t('deny-appeal') }}</span>
              </div>
            </div>
            <span class="label">{{ $t('reason-0') }}:</span>
            <GInput type="textarea" v-model="reason" maxlength="1000" :showCount="true"></GInput>
          </div>
        </div>
        <div class="modal-button-container">
          <button :class="[simpleEditValidationCheck ? '' : 'disabled', 'submit']" @click="editFieldForm">
            {{ $t('save') }}
          </button>
          <button @click="closeEditModal">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
    <Modal class="modal-screens" v-show="isResetFormModalOpen">
      <template v-slot:body class="body">
        {{ $t('reset-form-warning') }}
        <div class="reset-modal-button-container">
          <button @click="resetForm">{{ $t('reset') }}</button>
          <button @click="isResetFormModalOpen = false">{{ $t('cancel') }}</button>
        </div>
      </template>
    </Modal>
    <Modal class="modal-screens" v-show="isDialModalOpen">
      <template v-slot:body class="body" v-if="dialInfo">
        <div class="close" @click="isDialModalOpen = false"><Close /></div>
        <DialInfo :dialInfo="dialInfo" :period="selectedForm.config.period" />
      </template>
    </Modal>
  </div>
</template>

<script>
import 'vue-multiselect/dist/vue-multiselect.min.css';
import Multiselect from 'vue-multiselect';
import FieldAppealDataTable from '@/components/FieldAppealDataTable.vue';
import IconProgress from '@/components/IconProgress.vue';
import GInput from '@/components/GComponents/GInput.vue';
import Modal from '@/components/Modal.vue';
import DialInfo from '@/components/DialInfo.vue';
import Close from '@/assets/icons/x_icon_round.svg';
import { mapState } from 'vuex';
import fieldReviewTH from '@/utils/tableHeaders/fieldReviewTH';
import fieldReviewFr from '@/utils/tableHeaders/fieldReviewFr';
import Checkbox from '@/components/Checkbox.vue';
import SMMTFieldAppeal from '@/components/Dials/SMMTFieldAppeal.vue';
import SignatureIcon from '@/assets/icons/delete-icn.svg';

export default {
  name: 'FieldAppeal',
  components: {
    FieldAppealDataTable,
    Multiselect,
    IconProgress,
    GInput,
    Modal,
    DialInfo,
    Close,
    Checkbox,
    SMMTFieldAppeal,
    SignatureIcon,
  },
  props: {
    selectedForm: {
      type: Object,
    },
    formList: {
      type: Array,
    },
  },
  data() {
    return {
      value: 'appeals',
      domain: this.$store.state.global.programName,
      date: new Date(),
      saveClicked: false,
      form: null,
      rowCount: 7,
      currentPage: 1,
      stepRows: [],
      formInfo: null,
      appeals: [],
      formId: null,
      reason: null,
      result: null,
      title: null,
      options: {
        penColor: '#000000',
      },
      signature: null,
      invalidPermissions: false,
      lineItemOptions: [
        { label: this.$t('approve'), value: 'Approved' },
        { label: this.$t('deny'), value: 'Declined' },
        { label: 'Not Appealing', value: 'Not Appealing' },
        { label: this.$t('no-action'), value: 'no-action' },
      ],
      filterValue: null,
      notes: null,
      isModalOpen: false,
      isDialModalOpen: false,
      editMode: false,
      isEditModalOpen: false,
      isResetFormModalOpen: false,
      fieldReviewTH: fieldReviewTH(),
      fieldReviewFr: fieldReviewFr(),
      statusRows: [],
      dialInfo: null,
      comments: [],
      dialAppealReviewArr: [],
      isFormFinalized: false,
      allSmmtValid: false,
    };
  },
  methods: {
    smmtValidation(value) {
      this.allSmmtValid = false;
      if (value.isValid === true && this.$refs.smmtFieldAppeal && this.$refs.smmtFieldAppeal.length > 0) {
        this.allSmmtValid = this.$refs.smmtFieldAppeal.every((ref) => ref.fieldAppealIsValid === true);
      }
    },
    getComments() {
      const loader = this.$loading.show();

      const { formId } = this.selectedForm;

      this.$store
        .dispatch('getComments', { formId })
        .then((res) => {
          if (res) {
            this.comments = res.formComments;
          }
        })
        .catch((error) => {
          this.$notify({
            group: 'primary',
            type: 'error',
            text: error.data.error,
          });
        })
        .finally(() => {
          loader.hide();
        });
    },
    save() {
      if (this.submitValidationCheck) {
        this.saveNotes();
        const { reason, title } = this;
        const items = [];

        if (this.lineItemReviewer) {
          this.appeals.forEach((appeal) => {
            if (appeal.value) {
              items.push({
                dialId: appeal.dialId,
                result: appeal.value.value,
              });
            } else {
              items.push({
                dialId: appeal.dialId,
                result: 'Appeal Board',
              });
            }
          });
          this.result = 'Partial';
        }

        if (this.domain === 'US EL') {
          this.updateDialAppealReviewArr();
          if (this.mostRecentStatus.stepName === 'Dealer') {
            this.result = 'Complete';
          }
        }
        const params = {
          formId: this.selectedForm.formId,
          result: this.result,
          fieldSignature: this.signature,
          reason,
          title,
          items,
          dialAppealReviewArr: this.dialAppealReviewArr,
        };
        this.submitFieldForm(params);
      }
    },
    submitFieldForm(params) {
      const loader = this.$loading.show();

      this.$store
        .dispatch('updateForm', params)
        .then(() => {
          this.$notify({
            group: 'primary',
            type: 'success',
            text: this.$t('form-successfully-submitted'),
          });
          this.exit();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    editFieldForm() {
      if (this.simpleEditValidationCheck) {
        const loader = this.$loading.show();
        loader.hide();

        const params = {
          formId: this.selectedForm.formId,
          stepId: this.mostRecentStatus.stepName,
          statuses: [
            {
              result: this.result,
              dialId: null,
              reason: this.reason,
            },
          ],
          items: [],
        };

        this.$store
          .dispatch('editForm', params)
          .then(() => {
            this.$notify({
              group: 'primary',
              type: 'success',
              text: this.$t('successful-edit'),
            });
            this.isEditModalOpen = false;
            this.exit();
          })
          .catch((error) => {
            if (error.data) {
              this.$notify({
                group: 'primary',
                type: 'error',
                text: error.data.error,
              });
            }
          })
          .finally(() => {
            loader.hide();
          });
      }
    },
    editFieldFormWithLineItems() {
      if (this.lineItemEditValidationCheck) {
        const loader = this.$loading.show();

        const statuses = [];
        const items = [];

        this.appeals.forEach((appeal) => {
          if (appeal.value) {
            statuses.push({
              dialId: appeal.dialId,
              result: appeal.value.value,
            });
          }
          if (appeal.value.value === 'Not Appealing') {
            appeal.reasonForAppeal = 'Dealer is not appealing this dial.';
            appeal.notAppealed = true;
          } else {
            appeal.notAppealed = false;
          }
          const newObj = {
            reasonForAppeal: appeal.reasonForAppeal,
            dialId: appeal.dialId,
            notAppealed: appeal.notAppealed,
          };

          items.push(newObj);
        });

        const params = {
          formId: this.selectedForm.formId,
          stepId: this.mostRecentStatus.stepName,
          statuses,
          items,
        };

        this.$store
          .dispatch('editForm', params)
          .then(() => {
            this.$notify({
              group: 'primary',
              type: 'success',
              text: this.$t('successful-edit'),
            });
            this.editMode = false;
            this.getForm();
          })
          .catch((error) => {
            if (error.data) {
              this.$notify({
                group: 'primary',
                type: 'error',
                text: error.data.error,
              });
            }
          })
          .finally(() => {
            loader.hide();
          });
      }
    },
    updateDialAppealReviewArr() {
      let dialAppealId = null;
      let attributeId = null;
      let reviewResult = null;
      let reviewReason = null;
      let rowOrdinal = null;

      this.appeals.forEach((appeal, index) => {
        appeal.dialDataArr.forEach((dialData, index2) => {
          if (index === index2) {
            dialData.firstDetailSection.forEach((dialDataRow) => {
              dialDataRow.data.forEach((data) => {
                if (data.isMetricAppealable && data.isRowAppealable && data.selection !== undefined) {
                  attributeId = dialDataRow.dataName;
                  reviewResult = data.selection.value;
                  rowOrdinal = data.ordinal;
                  dialData.appealInfo[2].data.forEach((appealInfo) => {
                    if (rowOrdinal === appealInfo.ordinal) {
                      reviewReason = appealInfo.data;
                    }
                  });
                  // Set dial id
                  dialData.appealableDialResults.forEach((appealableRow) => {
                    if (rowOrdinal === appealableRow.ordinal) {
                      dialAppealId = appealableRow.id;
                    }
                  });
                  this.dialAppealReviewArr.push({
                    dialAppealId,
                    attributeId,
                    reviewResult,
                    reviewReason,
                  });
                }
              });
            });
          }
        });
      });
    },
    editLineItems() {
      this.editMode = true;
      document.getElementById('dials').scrollIntoView();
    },
    selectDial(dial) {
      const { entity } = this.selectedForm;
      const { period } = this.selectedForm.config;
      const domain = this.$store.state.global.programName;
      const dialIdObj = dial.dialId;
      const dialId = dialIdObj;

      if (dial.dialColor === 'FI' || dial.dialColor === 'X' || !dial.dialColor) {
        return;
      }
      const loader = this.$loading.show();
      this.$store.dispatch('getDial', { period, entity, dialId, domain }).then((res) => {
        this.isDialModalOpen = true;
        this.dialInfo = res[0];
        loader.hide();
      });
    },
    exit() {
      this.$emit('exit');
    },
    saveSignature() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      if (!isEmpty) {
        this.signature = data;
        // WIP
        // this.saveSignatureToStorage();
        // console.log('signature saved');
      }
    },
    clearSignature() {
      this.$refs.signaturePad.undoSignature();
      this.signature = null;
    },

    // Save signature to storage [WIP]

    // saveSignatureToStorage() {
    //   sessionStorage.setItem('userSignature', this.signature);
    // },
    // retrieveSignatureFromStorage() {
    //   const currUserSig = sessionStorage.getItem('userSignature');
    //   console.log('currUserSig: ', currUserSig);
    // },
    // deleteSignatureFromStorage() {
    //   sessionStorage.removeItem('userSignature');
    // },
    // clearDataFromStorage() {
    //   sessionStorage.clear();
    // },
    // loadSignature() {
    //   const data = sessionStorage.getItem('userSignature');
    //   const data2 = data.toString();
    //   console.log('signature data: ', data2);
    //   // const data = this.signature;
    //   // this.$refs.signaturePad.fromData(data);
    //   // console.log('signature loaded');
    //   // this.$refs.signaturePad.toData();
    //   this.$refs.signaturePad.fromDataURL(data2);
    //   console.log('signature loaded');
    // },
    setNotes() {
      const { notes } = this;
      this.isModalOpen = false;
      this.selectedForm.notes = notes;
      if (
        this.currentRoleName === 'Head of CX' ||
        this.currentRoleName === 'Executive Review' ||
        this.currentRoleName === 'Program Admin' ||
        this.currentRoleName === 'System Admin'
      ) {
        this.saveNotes();
      }
    },
    saveNotes() {
      const { notes } = this;

      const params = {
        formId: this.selectedForm.formId,
        notes,
        stepName: this.currentRoleName,
      };
      this.$store.dispatch('setNotes', params);
    },
    cancelNotes() {
      if (this.selectedForm.notes) {
        this.notes = this.selectedForm.notes;
      } else {
        this.notes = null;
      }
      this.isModalOpen = false;
    },
    getForm() {
      const loader = this.$loading.show();
      const { formId } = this.selectedForm;

      this.form = null;
      this.stepRows = [];
      this.statusRows = [];
      this.appeals = [];

      this.notes = this.selectedForm.notes;

      this.$store
        .dispatch('getForm', { formId })
        .then((res) => {
          if (res) {
            this.form = res;
            this.isFormFinalized = this.form.header[0].isFormFinalized;
            this.createFormStepsAndStatus();
            this.setUpLineItemReview();
            this.getComments();
          }
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    isEmpty(obj) {
      if (obj) {
        return Object.keys(obj).length === 0;
      }
      return false;
    },
    createFormStepsAndStatus() {
      this.form.steps.forEach((step, index) => {
        if (step.status && !this.isEmpty(step.status)) {
          this.$set(step.status, 'stepName', step.stepName);
          this.$set(step.status, 'ordinal', step.ordinal);
          if (this.form.header[0].isFormFinalized) {
            step.result = 'Completed';
          }
          this.stepRows.push(step.status);
          this.statusRows.push(step.status);
        } else if (
          index > 0 &&
          this.form.steps[index - 1].status &&
          !this.isEmpty(this.form.steps[index - 1].status) &&
          this.form.steps[index - 1].status.result !== 'Declined' &&
          (this.form.steps[index - 1].status.result !== 'Pending' ||
            this.form.steps[index - 1].status.result !== 'En attente')
        ) {
          this.stepRows.push({
            stepName: step.stepName,
            result: this.$t('pending'),
            ordinal: step.ordinal,
          });
        } else {
          this.stepRows.push({
            stepName: step.stepName,
            result: '',
            ordinal: step.ordinal,
          });
        }
      });
    },
    setUpLineItemReview() {
      if (this.lineItemReviewer) {
        this.result = 'true';
        this.form.items.forEach((item) => {
          item.disableDial = item.notAppealed === 'dealerNotAppealing';
          item.notAppealed = !!(
            item.notAppealed === '1' ||
            item.notAppealed === 'true' ||
            item.notAppealed === 'dealerNotAppealing' ||
            item.notAppealed === 'fieldNotAppealing'
          );
          if (item.notAppealed) {
            this.$set(item, 'value', {
              label: 'Not Appealing',
              value: 'Not Appealing',
            });
          } else if (item.statuses && item.statuses.result === 'Approved') {
            this.$set(item, 'value', {
              label: 'Approve',
              value: 'Approved',
            });
          } else if (item.statuses && item.statuses.result === 'Declined') {
            this.$set(item, 'value', {
              label: 'Deny',
              value: 'Declined',
            });
          } else {
            this.$set(item, 'value', {
              label: this.$t('no-action'),
              value: 'no-action',
            });
          }
          this.appeals.push(item);
        });
      } else {
        this.form.items.forEach((item) => {
          item.notAppealed = !!(
            item.notAppealed === '1' ||
            item.notAppealed === 'true' ||
            item.notAppealed === 'dealerNotAppealing' ||
            item.notAppealed === 'fieldNotAppealing'
          );
          this.appeals.push(item);
        });
      }
    },
    openEditModal() {
      this.isEditModalOpen = true;
    },
    closeEditModal() {
      this.reason = null;
      this.result = null;
      this.isEditModalOpen = false;
    },
    multiTargetOptions(targets) {
      const array = [
        { label: this.$t('deny'), value: 'Declined' },
        { label: 'Not Appealing', value: 'Not Appealing' },
        { label: this.$t('no-action'), value: 'no-action' },
      ];

      for (let i = 1; i <= targets; i += 1) {
        const obj = {
          label: this.$t('approve-to-target') + i,
          value: `Approve to target-${i}`,
        };
        array.push(obj);
      }

      return array;
    },
    resetForm() {
      const loader = this.$loading.show();
      const { formId } = this.selectedForm;
      this.$store
        .dispatch('resetForm', { formId })
        .then(() => {
          loader.hide();
          this.isResetFormModalOpen = false;
          this.getForm();
          this.$notify({
            group: 'primary',
            type: 'success',
            text: this.$t('form-reset-successful'),
          });
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        });
    },
  },
  computed: {
    ...mapState('global', ['programStyle']),
    lineItemReviewer() {
      if (this.form.permissions[0].canLineItemSubmit || this.form.permissions[0].canLineItemEdit) {
        return true;
      }
      return false;
    },
    name() {
      const user = JSON.parse(localStorage.getItem('stellantis-user'));
      const fullName = `${user.firstName} ${user.lastName}`;

      return fullName;
    },
    fullValidationCheck() {
      // return true;
      if (this.title && this.reason && this.signature && this.result) {
        return true;
      }
      return false;
    },
    simpleEditValidationCheck() {
      if (this.reason && this.result) {
        return true;
      }
      return false;
    },
    lineItemEditValidationCheck() {
      return this.reasonValidationCheck;
    },
    allItemsSelected() {
      let check = true;
      if (this.lineItemReviewer) {
        this.appeals.forEach((appeal) => {
          if (appeal.value.value === 'no-action') {
            check = false;
            return check;
          }
          return check;
        });
      }
      return check;
    },
    reasonValidationCheck() {
      let valid = true;
      this.appeals.forEach((appeal) => {
        if ((appeal.reason || appeal.reasonForAppeal || appeal.notAppealed) && valid) {
          valid = true;
        } else {
          valid = false;
        }
      });
      return valid;
    },
    submitValidationCheck() {
      if (this.domain !== 'US EL' && this.fullValidationCheck && this.allItemsSelected) {
        return true;
      }
      if (this.domain === 'US EL' && this.title && this.signature && this.allSmmtValid) {
        return true;
      }
      return false;
    },
    // Returns the most recent status that has an ID verifying that one has been submitted
    mostRecentStatus() {
      if (this.statusRows) {
        return this.statusRows.findLast((status) => status.id);
      }
      return null;
    },
    selectedLanguage() {
      return this.$store.state.languages.selectedLanguage.code;
    },
    tableHeadings() {
      return this.selectedLanguage === 'en' ? this.fieldReviewTH : this.fieldReviewFr;
    },
    checkboxLabel() {
      return this.$t('not-appealing');
    },
    checkboxReason() {
      return this.$t('not-appealing-dial');
    },
    currentRoleName() {
      return this.$store.state.global.roleName;
    },
  },
  watch: {},
  created() {
    this.getForm();
    // WIP
    // if (sessionStorage.getItem('userSignature')) {
    //   this.loadSignature();
    // }
    // this.getForm();
  },
};
</script>

<style lang="scss" scoped>
.content {
  color: #000000;
  text-align: left;
  margin: 40px 30px 20px 30px;
  .link {
    color: #243882;
  }
}
.appeal-header-container {
  h1 {
    margin-bottom: 30px;
  }
  h2 {
    margin-bottom: 20px;
  }
  .form-info {
    max-width: 100%;
    width: 800px;
    margin-bottom: 20px;
    border: 1px solid black;

    .item {
      margin: 10px;
      .label {
        font-weight: bold;
        margin-right: 5px;
      }
    }
  }
}
.appeal-status-container {
  margin: 0 -30px;
  border-bottom: 1px solid #00000033;
  .review-table {
    ::v-deep {
      thead {
        tr {
          th {
            &:first-child {
              padding-left: 30px;
            }
          }
        }
      }
      tbody {
        tr {
          td {
            &:first-child {
              padding-left: 30px;
            }
          }
        }
      }
    }
  }
}

.form-container {
  margin-top: 45px;
  .item-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 30px;
    border-bottom: 1px solid $border;
    padding-bottom: 30px;
    .appeal-info-container {
      display: flex;
      .left-container {
        .dial-container {
          text-align: center;
          margin-bottom: 10px;
          width: 80px;
          cursor: pointer;
          .smmtInner {
            left: -7px;
          }
        }
      }
      .right-container {
        width: 100%;
        margin-left: 10px;
        font-size: 14px;
        .description {
          margin: 10px 0;
          font-weight: bold;
        }
        input {
          height: 100px;
          width: initial;
          background: $input-background;
          border: none;
          margin-bottom: 20px;
          &:read-only {
            background: $input-readonly;
          }
        }
        .support-docs {
          margin-bottom: 10px;
          display: flex;
          align-items: center;
        }
      }
    }
    .helper-text-container {
      display: flex;
      flex-direction: column;
      margin-top: 15px;
      margin-left: 30px;
      .helper-text {
        font-size: 14px;
        &.red {
          color: $error;
        }
        .bold {
          font-weight: bold;
        }
      }
    }
  }
  .bottom-container {
    margin-bottom: 20px;
    .label {
      font-size: 15px;
    }
    .input-info h1 {
      margin-bottom: 25px;
    }
    .signature-label {
      font-weight: bold;
      margin: 10px 0px 20px;
    }
    .signature-section {
      width: 464px;
      border: 1px solid #a3a3a3;
    }
    .signature-input {
      align-items: flex-end;
      margin: 0px 40px;
      .signature-icon {
        margin-right: 10px;
        svg {
          height: 12px;
          width: 12px;
          path {
            fill: #d1d1d1 !important;
          }
        }
      }
      #signature {
        background: $primary-white;
        border-bottom: solid 1px #d1d1d1;
        width: 350px !important;
        ::v-deep {
          canvas {
            width: 350px !important;
          }
        }
      }
    }
    .signature-confirmation {
      font-size: 12px;
      margin-left: 60px;
    }
    .signature-buttons {
      justify-content: flex-end;
      margin: 20px 15px 15px;
      .edit-button {
        font-weight: normal;
        text-transform: none;
        font-size: 14px;
      }
      .right-btn {
        margin-left: 20px;
      }
    }
  }
}

.steps {
  margin-left: 30px;
}
.bottom-info {
  display: flex;
  flex-direction: column;
  margin-right: 30px;
  .info {
    margin-bottom: 15px;
  }
  input {
    width: initial;
    background: $input-background;
    border: none;
    margin-bottom: 20px;
    margin-right: 30px;
    border: 1px solid $light-grey;
    &:read-only {
      cursor: default;
      background: $input-readonly;
      border: none;
    }
    width: 250px;
  }
  .dial {
    margin-bottom: 40px;
    display: flex;
    align-items: center;
  }
  .label {
    margin-bottom: 20px;
  }
  .comments-container {
    margin: 34px 0px;
    .comments {
      font-size: 14px;
    }
    .comments:last-of-type {
      .comment-comma {
        visibility: hidden;
      }
    }
  }
  .notes-button-container {
    justify-content: flex-start;
    margin-bottom: 34px;
  }
}
.button-container {
  display: flex;
  margin-top: 45px;
  align-items: center;
  justify-content: flex-start;
  button {
    margin-right: 30px;
  }
}
.border-bottom {
  margin-bottom: 30px;
}
.border-bottom:last-child {
  border: none !important;
}

.modal-container {
  .bottom-container {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
  }
}
.modal-header {
  margin-bottom: 20px;
}
.modal-button-container {
  display: flex;
  margin-top: 10px;
  .submit {
    margin-right: 20px;
  }
}
.reset-modal-button-container {
  display: flex;
  justify-content: center;
  button {
    margin: 20px 10px 0px;
  }
}

.modal-body {
  display: flex;
  flex-direction: column;
  .close {
    display: flex;
    justify-content: flex-end;
    svg {
      height: 30px;
      width: 30px;
      cursor: pointer;
    }
  }
}
.checkbox-container {
  display: block;
  margin-bottom: 20px;
  ::v-deep {
    label {
      color: black;
    }
    input {
      z-index: 0 !important;
    }
  }
}
.notAppealed {
  color: $fca-gray;
}
.signature-buttons {
  margin-top: 20px;
  justify-content: flex-end;
  .edit-button {
    font-size: 16px;
    font-weight: bold;
    text-transform: uppercase;
    color: #243882;
    cursor: pointer;
  }
  .separator {
    font-weight: lighter;
    color: $fca-gray;
    margin: 0px 9.5px;
  }
  .disabled {
    opacity: 0.7;
    cursor: default;
    pointer-events: none;
  }
}
</style>
